import { JSX, useCallback, useState } from 'react';
import { DateTime } from 'luxon';
import type { IDeparture } from '../../../../models/departure.model';
import { TranslationService } from '../../../../services/translation.service';
import { DateHelper } from '../../../../helpers/date.helper';
import './style.scss';
import type { IDeparturesBoardItem } from './models/departure-board-item.model';
import LineIcon from '../../../Shared/LineIcon';
import useInterval from './hooks/use-interval.hook';
import { ApiPlaneraResaService } from '../../../../services/api-planera-resa/api-planera-resa.service';
import { ErrorResponseHelper } from '../../../../helpers/error-response.helper';
import type { ILocation } from '../../../../models/location';
import WheelchairIcon from '../../../Shared/WheelchairIcon';
import Spinner from '../../../Shared/Spinner';
import type { IErrorResponse } from '../../../../models/error-response.model';
import { ErrorHandlerHelper } from '../../../../helpers/error-handler.helper';
import { WheelchairIconType } from '../../../Shared/WheelchairIcon/wheelchair-icon-type.enum';

function departsAtOrAfter(departure: IDeparture, time: DateTime): boolean {
  if (departure.estimatedTime) {
    return DateTime.fromISO(departure.estimatedTime) >= time;
  }
  return DateTime.fromISO(departure.plannedTime) >= time;
}

function filterAndGroupDepartures(departures: IDeparture[], minimumDepartureTime: DateTime): IDeparturesBoardItem[] {
  const grouped: IDeparturesBoardItem[] = [];

  const filteredDepartures = departures.filter((d) => departsAtOrAfter(d, minimumDepartureTime));

  filteredDepartures.forEach((departure) => {
    let added = false;

    for (const groupElement of grouped) {
      if (
        groupElement.nextDeparture.serviceJourney.line?.shortName === departure.serviceJourney.line?.shortName &&
        groupElement.nextDeparture.serviceJourney.direction === departure.serviceJourney.direction
      ) {
        groupElement.thereAfterDeparture = departure;
        added = true;
        break;
      }
    }
    if (!added) {
      grouped.push({ nextDeparture: departure, thereAfterDeparture: undefined });
    }
  });

  return grouped;
}

function DepartureBoard({ stopArea }: { stopArea: ILocation }) {
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);
  const [departures] = useInterval<IDeparture[]>({
    callback: useCallback(async () => {
      try {
        const result = stopArea.gid ? await ApiPlaneraResaService.getDepartures(stopArea.gid, 200) : [];
        setErrorResponse(undefined);
        return result;
      } catch (error: unknown) {
        if (ErrorHandlerHelper.isErrorReponse(error)) {
          setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
        }
        return [];
      }
    }, [stopArea]),
    delay: 15000,
    dependency: stopArea,
  });
  const groupedDepartures = departures ? filterAndGroupDepartures(departures, DateTime.now()) : [];

  function getMinutesOrTextTo(departure: IDeparture): string {
    if (departure.isCancelled) {
      return TranslationService.get('departureboard.cancelled');
    }

    const timeDate = DateTime.fromISO(departure.estimatedTime || departure.plannedTime);

    const now = DateTime.now();
    const diffMinutes = DateHelper.getDurationInMinutes(now, timeDate);

    if (diffMinutes > 59) {
      return `${`0${timeDate.hour}`.slice(-2)}:${`0${timeDate.minute}`.slice(-2)}`;
    }
    return diffMinutes <= 0 ? TranslationService.get('common.now') : `${diffMinutes}`;
  }

  function renderThereafter(departure: IDeparture | undefined): JSX.Element {
    if (!departure) {
      return <></>;
    }
    return (
      <>
        {!departure.isCancelled && departure.serviceJourney.line?.isWheelchairAccessible && (
          <span className="departure-board__wheel-chair-icon d-inline-block me-1">
            <WheelchairIcon iconType={WheelchairIconType.white} sizeInRem={1.25} />
          </span>
        )}
        {departures?.length && (
          <span className={`departure-board__time ${departure.isCancelled ? 'departure-board__time--cancelled' : ''}`}>{departures.length > 1 ? getMinutesOrTextTo(departure) : ''}</span>
        )}
      </>
    );
  }

  return (
    <div className="departure-board h-100">
      {departures !== undefined && departures.length > 0 && (
        <table aria-live="polite" className="text-color-white w-100 departure-board__table">
          <thead>
            <tr>
              <th colSpan={2} className="pb-1 ps-2 fw-normal" scope="colgroup">
                {TranslationService.get('departureboard.line')}
              </th>
              <th className="ps-2 text-nowrap fw-normal" scope="col">
                {TranslationService.get('departureboard.next')} (min)
              </th>
              <th className="d-none d-sm-table-cell ps-2 departure-board__thereafter-td fw-normal" scope="col">
                {TranslationService.get('departureboard.thereafter')}
              </th>
              <th className="text-center px-2 fw-normal" scope="col">
                {TranslationService.get('departureboard.platform')}
              </th>
            </tr>
          </thead>
          <tbody>
            {groupedDepartures.map((departure) => (
              <tr key={departure.nextDeparture.serviceJourney.gid}>
                <td className="py-3 ps-2 departure-board__line-icon-td">
                  {departure.nextDeparture.serviceJourney.line && (
                    <LineIcon line={departure.nextDeparture.serviceJourney.line} className="text-center text-nowrap d-inline-block departure-board__line-icon" />
                  )}
                </td>
                <td className="ps-2">
                  <span
                    className={`d-inline-block departure-board__direction ${
                      departure.nextDeparture.serviceJourney.via && departure.nextDeparture.serviceJourney.via.length > 0 ? 'departure-board__line-icon-via' : ''
                    }`}
                  >
                    {departure.nextDeparture.serviceJourney.direction}
                    {departure.nextDeparture.serviceJourney.via && departure.nextDeparture.serviceJourney.via.length > 0 && (
                      <em className="small">
                        <br />
                        {departure.nextDeparture.serviceJourney.via}
                      </em>
                    )}
                  </span>
                </td>
                <td className="ps-2 departure-board__next-td">
                  {!departure.nextDeparture.isCancelled && departure.nextDeparture.serviceJourney.line?.isWheelchairAccessible && (
                    <span className="departure-board__wheel-chair-icon d-inline-block me-1">
                      <WheelchairIcon iconType={WheelchairIconType.white} sizeInRem={1.25} />
                    </span>
                  )}
                  <span className={`departure-board__time fw-bold ${departure.nextDeparture.isCancelled ? 'departure-board__time--cancelled' : ''}`}>
                    {getMinutesOrTextTo(departure.nextDeparture)}
                  </span>
                </td>
                <td className="ps-2 d-none d-sm-table-cell departure-board__thereafter-td">{renderThereafter(departure.thereAfterDeparture)}</td>
                <td className="px-2 text-center departure-board__track-td fw-bold">{departure.nextDeparture.platform}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(departures === undefined || departures.length === 0) && (
        <div className="h-100 d-flex align-items-center justify-content-center text-white">
          {departures !== undefined && departures.length === 0 && !errorResponse && <span className="text-center px-2">{TranslationService.get('departureboard.nodata')}</span>}
          {departures === undefined && <Spinner delayInMs={100} />}
          {errorResponse !== undefined && (errorResponse.statusCode === 503 || errorResponse.statusCode === 500) && (
            <span className="text-center px-2">{errorResponse?.errorMessage || TranslationService.get('common.generalerror')}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default DepartureBoard;
